import type { AppySnapshot } from './model'

export type PlanKey = 'starter' | 'creator' | 'studio'

export interface PlanFeatures {
  key: PlanKey
  label: string
  /** Max words in a text post / short caption. */
  maxPostWords: number
  /** Max characters in a text post (hard cap for DB). */
  maxPostChars: number
  /** Max image upload size in bytes. */
  maxImageBytes: number
  /** Max video/short upload size in bytes. */
  maxVideoBytes: number
  /** Max short duration seconds. */
  maxShortSeconds: number
  /** Long-form editing, edit window, etc. */
  longFormPosts: boolean
  hdMedia: boolean
  priorityBuild: boolean
  advancedAnalytics: boolean
  longerCaptionsOnApps: boolean
  shortsEnabled: boolean
  multipleImages: boolean
  monthlyCreditsLabel: string
  priceLabel: string
  features: string[]
}

const MB = 1024 * 1024

export const PLAN_FEATURES: Record<PlanKey, PlanFeatures> = {
  starter: {
    key: 'starter',
    label: 'Free',
    maxPostWords: 1000,
    maxPostChars: 8_000,
    maxImageBytes: 8 * MB,
    maxVideoBytes: 40 * MB,
    maxShortSeconds: 30,
    longFormPosts: false,
    hdMedia: false,
    priorityBuild: false,
    advancedAnalytics: false,
    longerCaptionsOnApps: false,
    shortsEnabled: true,
    multipleImages: false,
    monthlyCreditsLabel: 'Pay-as-you-go credits',
    priceLabel: 'Free',
    features: [
      'Post text, images, and Shorts',
      'Up to 1,000 words per post',
      'Images up to 8MB',
      'Shorts up to 30 seconds',
      'Build apps & games with credits',
      'Basic For You ranking',
    ],
  },
  creator: {
    key: 'creator',
    label: 'Creator',
    maxPostWords: 25_000,
    maxPostChars: 150_000,
    maxImageBytes: 32 * MB,
    maxVideoBytes: 150 * MB,
    maxShortSeconds: 60,
    longFormPosts: true,
    hdMedia: true,
    priorityBuild: false,
    advancedAnalytics: false,
    longerCaptionsOnApps: true,
    shortsEnabled: true,
    multipleImages: true,
    monthlyCreditsLabel: '$18 monthly build credits',
    priceLabel: '$24/mo',
    features: [
      'Everything on Free',
      'Up to 25,000 words per post',
      'HD images up to 32MB',
      'Shorts up to 60 seconds',
      'Long-form posts & app captions',
      'Low, medium, and high intelligence',
      'Daily Game controls',
      'Version memory and editing',
    ],
  },
  studio: {
    key: 'studio',
    label: 'Pro',
    maxPostWords: 1_000_000,
    maxPostChars: 6_000_000,
    maxImageBytes: 100 * MB,
    maxVideoBytes: 500 * MB,
    maxShortSeconds: 180,
    longFormPosts: true,
    hdMedia: true,
    priorityBuild: true,
    advancedAnalytics: true,
    longerCaptionsOnApps: true,
    shortsEnabled: true,
    multipleImages: true,
    monthlyCreditsLabel: '$24 monthly build credits',
    priceLabel: '$34/mo',
    features: [
      'Everything in Creator',
      'Up to 1,000,000 words per post',
      'Ultra media: images to 100MB, video to 500MB',
      'Shorts up to 3 minutes',
      'Priority AI builds',
      'Higher generation usage limits',
      'Advanced feed signals & analytics',
      'Best for power posters & studios',
    ],
  },
}

export const planFromBilling = (plan: AppySnapshot['billing']['plan'] | string | undefined): PlanKey => {
  if (plan === 'studio' || plan === 'pro') return 'studio'
  if (plan === 'creator') return 'creator'
  return 'starter'
}

export const featuresForPlan = (plan: AppySnapshot['billing']['plan'] | string | undefined): PlanFeatures =>
  PLAN_FEATURES[planFromBilling(plan)]

export const countWords = (text: string): number =>
  text.trim() ? text.trim().split(/\s+/).filter(Boolean).length : 0

export const formatBytes = (bytes: number): string => {
  if (bytes >= MB) return `${Math.round(bytes / MB)}MB`
  return `${Math.round(bytes / 1024)}KB`
}
